import { useCallback } from 'react';
import { useChat } from '../../../../../libs/hooks';
import { IChatMessage, UserFeedback, UserFeedbackType } from '../../../../../libs/models/ChatMessage';
import { useAppDispatch } from '../../../../../redux/app/hooks';
import { addUserFeedback } from '../../../../../redux/features/conversations/conversationsSlice';

interface IUserFeedbackProps {
    message: IChatMessage;
    userFeedback?: UserFeedback;
    onFeedbackTypeProvided: (feedbackType: boolean) => void;
}

export const UserFeedbackActions: React.FC<IUserFeedbackProps> = ({
    message,
    userFeedback,
    onFeedbackTypeProvided,
}) => {
    const dispatch = useAppDispatch();
    const chat = useChat();

    const onUserFeedbackProvided = useCallback(
        async (positive: boolean) => {
            onFeedbackTypeProvided(positive);

            if (message.id) {
                await chat
                    .editMessage(
                        message.chatId,
                        message.id,
                        positive ? UserFeedbackType.Positive : UserFeedbackType.Negative,
                        undefined,
                    )
                    .then((result: UserFeedback) => {
                        dispatch(
                            addUserFeedback({
                                chatId: message.chatId,
                                userFeedback: result,
                            }),
                        );
                    });
            }
        },
        [dispatch, chat, onFeedbackTypeProvided, message],
    );

    const getFeedbackControls = () => {
        if (userFeedback?.type === UserFeedbackType.Positive) {
            return (
                <span className="stroke-primary-300">
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-5 w-5"
                        viewBox="0 0 24 24"
                        strokeWidth="2"
                        fill="none"
                        strokeLinecap="round"
                        strokeLinejoin="round">
                        <title>Good response</title>
                        <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                        <path d="M7 11v8a1 1 0 0 1 -1 1h-2a1 1 0 0 1 -1 -1v-7a1 1 0 0 1 1 -1h3a4 4 0 0 0 4 -4v-1a2 2 0 0 1 4 0v5h3a2 2 0 0 1 2 2l-1 5a2 3 0 0 1 -2 2h-7a3 3 0 0 1 -3 -3"></path>
                    </svg>
                </span>
            );
        } else if (userFeedback?.type === UserFeedbackType.Negative) {
            return (
                <span className="stroke-primary-300">
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-5 w-5"
                        viewBox="0 0 24 24"
                        strokeWidth="2"
                        fill="none"
                        strokeLinecap="round"
                        strokeLinejoin="round">
                        <title>Bad response</title>
                        <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                        <path d="M7 13v-8a1 1 0 0 0 -1 -1h-2a1 1 0 0 0 -1 1v7a1 1 0 0 0 1 1h3a4 4 0 0 1 4 4v1a2 2 0 0 0 4 0v-5h3a2 2 0 0 0 2 -2l-1 -5a2 3 0 0 0 -2 -2h-7a3 3 0 0 0 -3 3"></path>
                    </svg>
                </span>
            );
        } else {
            return (
                <>
                    <button
                        title="Good response"
                        onClick={() => void onUserFeedbackProvided(true)}
                        type="button"
                        className="dark:hover:text-slate-50 hover:text-primary-700 transition-colors duration-200">
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-5 w-5 hover:fill-primary-500"
                            viewBox="0 0 24 24"
                            strokeWidth="2"
                            stroke="currentColor"
                            fill="none"
                            strokeLinecap="round"
                            strokeLinejoin="round">
                            <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                            <path d="M7 11v8a1 1 0 0 1 -1 1h-2a1 1 0 0 1 -1 -1v-7a1 1 0 0 1 1 -1h3a4 4 0 0 0 4 -4v-1a2 2 0 0 1 4 0v5h3a2 2 0 0 1 2 2l-1 5a2 3 0 0 1 -2 2h-7a3 3 0 0 1 -3 -3"></path>
                        </svg>
                    </button>
                    <button
                        onClick={() => void onUserFeedbackProvided(false)}
                        type="button"
                        className="dark:hover:text-slate-50 hover:text-primary-700 transition-colors duration-200">
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-5 w-5 hover:fill-primary-500"
                            viewBox="0 0 24 24"
                            strokeWidth="2"
                            stroke="currentColor"
                            fill="none"
                            strokeLinecap="round"
                            strokeLinejoin="round">
                            <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                            <path d="M7 13v-8a1 1 0 0 0 -1 -1h-2a1 1 0 0 0 -1 1v7a1 1 0 0 0 1 1h3a4 4 0 0 1 4 4v1a2 2 0 0 0 4 0v-5h3a2 2 0 0 0 2 -2l-1 -5a2 3 0 0 0 -2 -2h-7a3 3 0 0 0 -3 3"></path>
                        </svg>
                    </button>
                </>
            );
        }
    };

    return getFeedbackControls();
};
